import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import store from '../../../store';

import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional} from '../../../util/conditional';
import {
	selectRestrictionDimmingFactorBySeason,
	selectSolarActivityLevelData,
	selectSolarAzimuthData, selectSolarBatteryNumber, selectSolarCalculated,
	selectSolarDimmProfileData, selectSolarHousingNumber,
	selectSolarLuminarCountValue,
	selectSolarLumPosition1,
	selectSolarLumPosition2,
	selectSolarNumber1, selectSolarNumber2, selectSolarSelfSufficiency,
	selectSolarShadowLevelData, selectSolarUnitHeight, selectSolarUnitNumber, selectSolarVersion
} from '../../solarSelectors';
import {selectSpcConfiguration} from '../../../selectors';
import {saveTenderText} from '../../../slices/configurationSlice';

@customElement('solar-tender-text')
export class SolarTenderText extends connect(store)(LitElement) {

	static styles = css`
		.solarTenderText {
			padding-bottom: 15px;
		}

		p {
			margin: 0 0 10px 0;
			font: var(--font-specsheet-light);
			line-height: 140%;
		}

		.info {
			font: var(--font-specsheet-small-light);
			margin-bottom: 25px;
		}

		@media print {
			p {
				margin: 0 0 1.5mm 0;
				font: var(--font-specsheet-print-light);
				line-height: 120%;
			}

			.info {
				font: var(--font-specsheet-print-small-text);
			}
		}

		:host(.light-pdf) p {
			font: var(--font-specsheet-print-light);
		}

	`;

	static properties = {
		class: {type: String},
	};

	tenderTextRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.lang = state.status.lang;
		this.state = state;
		this.debug = state.status.debug;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
		this.class = this.getAttribute('class') || '';

		this.solar = state.solar.data;
		this.generators = parseInt(selectSolarUnitHeight(state), 10);
		this.location = this.solar.project.location;
		this.orientation = this.solar.project.orientation;
		this.selfSufficiency = selectSolarSelfSufficiency(state);
		this.solarVersion = selectSolarVersion(state);
		this.isPro = selectSolarVersion(state) === 'AetoPro';
		this.isBasic = selectSolarVersion(state) === 'AetoBasic';

		if (this.location && this.orientation) {
			this.lumCount = selectSolarLuminarCountValue(state) ?? 1;
			this.restrictionDimmingFactorBySeason = selectRestrictionDimmingFactorBySeason(this.state);
		}
		this.baseHeight = parseInt(getValueOfFeature('HS_HousingHeight', this.spcConfiguration), 10) / 10;
		this.fullHeight = this.baseHeight + this.generators;
		this.lum1Height = this.baseHeight + parseInt(selectSolarLumPosition1(state), 10);
		this.lum2Height = this.baseHeight + parseInt(selectSolarLumPosition2(state), 10);

		this.calculated = selectSolarCalculated(state);
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('copyToClipboard', () => this.copyToClipboard());
	}

	disconnectedCallback() {
		window.removeEventListener('copyToClipboard', () => this.copyToClipboard());
		super.disconnectedCallback();
	}
	firstUpdated(changedProperties) {
		console.log('firstUpdated solarTenderText', this.tenderTextRef);
		const tenderText = this.tenderTextRef.value.innerText;
		const cleanedText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		store.dispatch(saveTenderText({tenderText: cleanedText}));
	}
	copyToClipboard() {
		let tenderText = this.tenderTextRef.value.innerText;

		// clean lineBreaks and spaces
		tenderText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');

		navigator.clipboard.writeText(tenderText).then(
			() => {
			},
			(err) => {
				console.error('Async: Could not copy text: ', err);
			});
	}

	// shortcut for translated text
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		if (!this.location || !this.orientation || !this.restrictionDimmingFactorBySeason) return null;
		return html`
			<div class="solarTenderText" ${ref(this.tenderTextRef)}>
				<p>
					${filterAndJoin([
						this.t('HS_HousingSize', 'FV.S', null, '', ' '),
						this.t('HS_HousingType', 'FV.S', null, '', ''),
						this.t('HS_Construction', 'FV.S', null, ', '),
					], '')}
				</p>
				<p>
					<trans-label key="LU_Product_Description" context="D.S"></trans-label>
				</p>
				<p>
					${msg('Location')}: ${this.location?.name}<br>
					${msg('Orientation')}: ${selectSolarAzimuthData(this.state).label[this.lang]}, ${msg('Shading')}:
					${selectSolarShadowLevelData(this.state).label[this.lang]}<br>
					${msg('Lighting profile')}: ${selectSolarDimmProfileData(this.state).label[this.lang]}<br>
					${msg('Self Sufficient Days')}: ${when(this.isPro,
					() => html`
						${msg('Summer')}: ${this.selfSufficiency[this.solarVersion].summer},
						${msg('Spring/Fall')}: ${this.selfSufficiency[this.solarVersion].spring},
						${msg('Winter')}: ${this.selfSufficiency[this.solarVersion].winter}
					`,
					() => html`
						${this.selfSufficiency[this.solarVersion].summer}
					`
				)}<br>
					<trans-label slot="label" key="EB_MotionSensor_1" context="F"></trans-label>
					:
					${filterAndJoin([
						selectSolarActivityLevelData(this.state).label[this.lang],
						selectSolarActivityLevelData(this.state).description[this.lang]
					], ', ')}<br>
					${filterAndJoin([
						this.t('LU_SolarUnit', 'F', null, '', ':'),
						this.t('LU_SolarUnit', 'FV.S')
					], ' ')}<br>
					${filterAndJoin([
						this.t('LE_LightDistribution_1', 'F', null, '', ':'),
						this.t('LE_LightDistribution_1', 'FV.S'),
					], ' ')}<br>
					${filterAndJoin([
						this.t('LE_ColourTemperature_1', 'F', null, '', ':'),
						this.t('LE_ColourTemperature_1', 'FV.S'),
						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV'),
					], '')}
				</p>

				<p>${msg('Power')} (max):<br>
					${when(
						this.isBasic,
						() => html`
							${msg('Full Year Profile (max)')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.lum} lm
							<br>
						`,
						() => html`
							${msg('Summer')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.summer.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.summer.lum} lm
							<br>
							${msg('Autumn')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.spring.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.spring.lum} lm
							<br>
							${msg('Winter')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.power} W, ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.calculated.winter.lum} lm
							<br>
						`)}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_HousingSize', 'FV.L'),
						this.t('HS_LightEmission', 'FV.L'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('LU_SolarUnit', 'F', null, '', ':'),
						this.t('LU_SolarUnit', 'FV.L')
					], ' ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('LU_Battery', 'F'),
						this.t('LU_Battery', 'FV.L')
					], ': ')}
				</p>
				<p>
					<trans-label key="LU_BaseTube_Description" context="D.S"></trans-label>
				</p>

				<p>
					${this.t('EB_ConstantLumenTechnology_1', 'F', null, '', ':')}
					${this.t('EB_ConstantLumenTechnology_1', 'FV.S')}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_HousingColour', 'F', null, '', ': ') +
						this.t('HS_HousingColour', 'FV')
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_HousingCoating', 'F', null, '', ': ') +
						this.t('HS_HousingCoating', 'FV')
					], ', ')}
				</p>


				<p>
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('LU_LuminaireMaterial.die-cast_aluminium', 'DV.L'),
						this.t('HS_HousingDescription', 'D.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
						this.t('HS_HousingPowerConnection', 'FV.S'),
						this.t('HS_Attachement.Avanza', 'DV.L')
					], ', ')}
				</p>

				<p>
					${this.t('LU_SurgeProtection', 'D')}
					${this.t('LU_SurgeProtection.10kV', 'DV.S', null, '', ',')}
					${this.t('LU_TechnicalRegion', 'D')}
					${this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', ',')}
					${this.t('LU_AmbientTemperature', 'D')}
					${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S')}
				</p>
				<p>
					${msg('Luminaire Height')}:
					${filterAndJoin([
						this.lumCount > 1 ? this.lum2Height.toLocaleString() + ' m' : null,
						this.lum1Height.toLocaleString() + ' m',
					], ' & ')}<br>
					${filterAndJoin([
						this.t('HS_HousingHeight', 'F') + this.t('HS_HousingHeight', 'FV', null, ' ',),
					], ', ')}<br>
					${msg('Total Height')}: ${this.fullHeight.toLocaleString()} m<br>
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_Base', 'F'),
						this.t('HS_Base', 'FV.S')
					], ': ')}
				</p>
				<p>
					<!-- join -->
					${filterAndJoin([
						this.t('LU_IP.IP66', 'DV.S'),
						this.t('LU_IK.10', 'DV.S'),
						this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
						this.t(this.c('LU_ENEC_CertificateDescription.ENEC'), 'DV.S'),
						this.t('LU_CE.CE', 'DV.S'),
					], ', ')}
				</p>
				<p>
					${msg('Manufacturer')}: Selux
				</p>
				<p class="info">
					${selectSolarNumber1(s)}, ${selectSolarNumber2(s)}<br>
					Housing: ${selectSolarHousingNumber(s)}<br>
					Battery: ${selectSolarBatteryNumber(s)}<br>
					SolarUnit: ${selectSolarUnitNumber(s)}<br>
				</p>
			</div>
		`;
	}
}
